import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { ChevronLeft, Search, SortAsc, SortDesc, CheckCircle, XCircle, Calendar, Tag, MessageSquare, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GradeWithDetails, StudentWithClass } from '../../types/supabase';

type SortField = 'subject' | 'grade' | 'date' | 'exam_title';
type SortOrder = 'asc' | 'desc';

// Type for subject average calculations
type SubjectAverage = {
  id: string;
  name: string;
  average: number;
  count: number;
  absences: number;
  coefficient: number;
  class_name?: string;
};

const StudentGrades = () => {
  const { user } = useAuth();
  const [grades, setGrades] = useState<GradeWithDetails[]>([]);
  const [student, setStudent] = useState<StudentWithClass | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [classFilter, setClassFilter] = useState<string>('');
  const [examTypeFilter, setExamTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [uniqueClasses, setUniqueClasses] = useState<{id: string, name: string}[]>([]);
  const [uniqueExamTypes, setUniqueExamTypes] = useState<string[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchStudentAndGrades();
    }
  }, [user]);

  useEffect(() => {
    if (grades.length > 0) {
      extractUniqueFilters();
    }
  }, [grades]);

  const extractUniqueFilters = () => {
    // Extract unique classes
    const classesMap = new Map<string, {id: string, name: string}>();
    
    grades.forEach(grade => {
      if (grade.subject.class) {
        const classObj = grade.subject.class;
        const classDisplayName = `${classObj.name} ${classObj.class_number} (${classObj.period})`;
        classesMap.set(classObj.id, {
          id: classObj.id,
          name: classDisplayName
        });
      }
    });
    
    setUniqueClasses(Array.from(classesMap.values()));

    // Extract unique exam types
    const examTypes = new Set<string>();
    grades.forEach(grade => {
      if (grade.exam_type) {
        examTypes.add(grade.exam_type);
      }
    });
    setUniqueExamTypes(Array.from(examTypes));
  };

  const fetchStudentAndGrades = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    try {
      // First get the student record for this user
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select(`
          *,
          class:classes(id, name, class_number, period)
        `)
        .eq('email', user.email)
        .single();

      if (studentError) {
        throw studentError;
      }

      if (!studentData) {
        setError('Aucun profil étudiant trouvé pour cet utilisateur');
        setIsLoading(false);
        return;
      }

      setStudent(studentData as StudentWithClass);

      // Get grades for this student with subject and trainer info
      const { data, error } = await supabase
        .from('grades')
        .select(`
          *,
          student:students(first_name, last_name, email),
          subject:subjects(name, description, coefficient, class:classes(id, name, class_number, period))
        `)
        .eq('student_id', studentData.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setGrades(data as GradeWithDetails[]);
    } catch (error) {
      console.error('Error fetching grades:', error);
      setError('Erreur lors du chargement des notes');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Format class display name
  const formatClassDisplay = (classObj: any) => {
    if (!classObj) return '';
    return `${classObj.name} ${classObj.class_number} (${classObj.period})`;
  };

  const getSortedGrades = () => {
    const filtered = grades.filter(grade => {
      const subjectName = grade.subject.name.toLowerCase();
      const examTitle = grade.exam_title?.toLowerCase() || '';
      const term = searchTerm.toLowerCase();
      const searchMatches = subjectName.includes(term) || examTitle.includes(term);
      
      // Apply class filter
      const classMatches = !classFilter || 
                           (grade.subject.class && grade.subject.class.id === classFilter);
      
      // Apply exam type filter
      const typeMatches = !examTypeFilter || grade.exam_type === examTypeFilter;
      
      return searchMatches && classMatches && typeMatches;
    });

    return [...filtered].sort((a, b) => {
      if (sortField === 'subject') {
        const comparison = a.subject.name.localeCompare(b.subject.name);
        return sortOrder === 'asc' ? comparison : -comparison;
      } else if (sortField === 'grade') {
        // Absences always at bottom when sorting by grade
        if (a.is_absent && !b.is_absent) return sortOrder === 'asc' ? 1 : -1;
        if (!a.is_absent && b.is_absent) return sortOrder === 'asc' ? -1 : 1;
        if (a.is_absent && b.is_absent) return 0;
        
        // Take coefficient into account when sorting by grade
        const valueA = (a.grade || 0) * (a.coefficient || 1);
        const valueB = (b.grade || 0) * (b.coefficient || 1);
        const comparison = valueA - valueB;
        return sortOrder === 'asc' ? comparison : -comparison;
      } else if (sortField === 'date') {
        // Use exam_date if available, otherwise fall back to created_at
        const dateA = a.exam_date ? new Date(a.exam_date).getTime() : new Date(a.created_at).getTime();
        const dateB = b.exam_date ? new Date(b.exam_date).getTime() : new Date(b.created_at).getTime();
        const comparison = dateA - dateB;
        return sortOrder === 'asc' ? comparison : -comparison;
      } else if (sortField === 'exam_title') {
        const titleA = a.exam_title || '';
        const titleB = b.exam_title || '';
        const comparison = titleA.localeCompare(titleB);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      return 0;
    });
  };

  const sortedGrades = getSortedGrades();

  // Calculate average by subject with coefficient
  const getAveragesBySubject = (): SubjectAverage[] => {
    const subjects: Record<string, { 
      id: string;
      total: number; 
      totalCoef: number; 
      count: number; 
      name: string;
      absences: number;
      coefficient: number;
      class_name?: string;
    }> = {};
    
    grades.forEach(grade => {
      // Apply filters
      const classMatches = !classFilter || 
                          (grade.subject.class && grade.subject.class.id === classFilter);
      const typeMatches = !examTypeFilter || grade.exam_type === examTypeFilter;
      
      if (!classMatches || !typeMatches) return;
      
      if (!subjects[grade.subject_id]) {
        subjects[grade.subject_id] = { 
          id: grade.subject_id,
          total: 0, 
          totalCoef: 0,
          count: 0, 
          name: grade.subject.name,
          absences: 0,
          coefficient: grade.subject.coefficient || 1,
          class_name: grade.subject.class ? formatClassDisplay(grade.subject.class) : undefined
        };
      }
      
      if (grade.is_absent) {
        subjects[grade.subject_id].absences += 1;
      } else {
        const coef = grade.coefficient || 1;
        subjects[grade.subject_id].total += (grade.grade || 0) * coef;
        subjects[grade.subject_id].totalCoef += coef;
        subjects[grade.subject_id].count += 1;
      }
    });

    return Object.values(subjects).map(subject => ({
      id: subject.id,
      name: subject.name,
      average: subject.totalCoef > 0 ? Math.round((subject.total / subject.totalCoef) * 10) / 10 : 0,
      count: subject.count,
      absences: subject.absences,
      coefficient: subject.coefficient,
      class_name: subject.class_name
    }));
  };

  const subjectAverages = getAveragesBySubject();

  // Calculate overall average taking subject coefficients into account
  const calculateGeneralAverage = () => {
    const averagesBySubject = getAveragesBySubject();
    let totalWeightedAverage = 0;
    let totalWeight = 0;

    averagesBySubject.forEach(subject => {
      if (subject.count > 0) { // Only include subjects that have grades
        const subjectCoef = subject.coefficient;
        totalWeightedAverage += subject.average * subjectCoef;
        totalWeight += subjectCoef;
      }
    });

    return totalWeight > 0 ? Math.round((totalWeightedAverage / totalWeight) * 10) / 10 : 0;
  };

  const generalAverage = calculateGeneralAverage();

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Helper function to render grade or absent status
  const renderGradeValue = (grade: GradeWithDetails) => {
    if (grade.is_absent) {
      return <span className="font-semibold text-gray-500">ABS</span>;
    } else {
      return (
        <span 
          className={`font-semibold ${
            (grade.grade || 0) >= 10 ? 'text-success-500' : 'text-error-500'
          }`}
        >
          {grade.grade}/20
        </span>
      );
    }
  };

  // Helper function to get exam type display text
  const getExamTypeText = (type: string | null) => {
    if (!type) return '-';
    
    const types: Record<string, string> = {
      'devoir_surveille': 'Devoir surveillé',
      'devoir_maison': 'Devoir maison',
      'participation': 'Participation',
      'examen_blanc': 'Examen blanc',
      'examen_officiel': 'Examen officiel'
    };
    
    return types[type] || type;
  };

  // Helper function to get exam method display text
  const getMethodText = (method: string | null) => {
    if (!method) return '-';
    
    return method === 'ecrit' ? 'Écrit' : method === 'oral' ? 'Oral' : method;
  };

  const toggleGradeDetails = (gradeId: string) => {
    setSelectedGrade(selectedGrade === gradeId ? null : gradeId);
  };

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/student" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span>Retour au tableau de bord</span>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Mes notes</h1>
        <p className="mt-2 text-gray-600">
          {student?.class ? `Classe: ${formatClassDisplay(student.class)}` : 'Consultez toutes vos notes par matière'}
        </p>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="mb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 p-4 border-b">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full">
                <div className="relative w-full md:w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="input-field pl-10 py-1"
                    placeholder="Rechercher par matière ou examen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <button 
                  onClick={() => setShowFilters(!showFilters)} 
                  className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline'} flex items-center`}
                >
                  <Filter className="h-4 w-4 mr-1" />
                  <span>Filtres</span>
                </button>
                
                {(classFilter || examTypeFilter) && (
                  <button 
                    onClick={() => {
                      setClassFilter('');
                      setExamTypeFilter('');
                    }}
                    className="btn btn-sm btn-outline flex items-center"
                  >
                    <X className="h-4 w-4 mr-1" />
                    <span>Effacer</span>
                  </button>
                )}
              </div>
            </div>

            {showFilters && (
              <div className="bg-gray-50 p-4 mb-4 animate-fade-in border-b">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="classFilter" className="block text-sm font-medium text-gray-700 mb-1">
                      Filtrer par classe:
                    </label>
                    <select
                      id="classFilter"
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Toutes les classes</option>
                      {uniqueClasses.map((classObj) => (
                        <option key={classObj.id} value={classObj.id}>
                          {classObj.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="examTypeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                      Filtrer par type d'examen:
                    </label>
                    <select
                      id="examTypeFilter"
                      value={examTypeFilter}
                      onChange={(e) => setExamTypeFilter(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Tous les types</option>
                      <option value="devoir_surveille">Devoir surveillé</option>
                      <option value="devoir_maison">Devoir maison</option>
                      <option value="participation">Participation</option>
                      <option value="examen_blanc">Examen blanc</option>
                      <option value="examen_officiel">Examen officiel</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="divide-y divide-gray-200">
              {isLoading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : sortedGrades.length === 0 ? (
                <div className="text-center py-8">
                  {searchTerm || classFilter || examTypeFilter 
                    ? 'Aucun résultat trouvé' 
                    : 'Vous n\'avez pas encore de notes'}
                </div>
              ) : (
                <>
                  {sortedGrades.map((grade) => (
                    <div key={grade.id} className="group">
                      <div 
                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${
                          selectedGrade === grade.id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleGradeDetails(grade.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900">{grade.subject.name}</h3>
                                <p className="text-sm text-gray-500">
                                  {formatDate(grade.exam_date)}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="flex items-center gap-2">
                                  {renderGradeValue(grade)}
                                  <span className="text-xs text-gray-500">
                                    (coef. {grade.coefficient || 1})
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded details */}
                      {selectedGrade === grade.id && (
                        <div className="p-4 bg-blue-50 border-t border-blue-100 animate-fade-in">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-3">Détails de l'évaluation</h4>
                              <dl className="space-y-2 text-sm">
                                <div>
                                  <dt className="text-gray-500">Classe</dt>
                                  <dd className="font-medium">
                                    {grade.subject.class ? formatClassDisplay(grade.subject.class) : '-'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-gray-500">Type d'examen</dt>
                                  <dd className="font-medium">{getExamTypeText(grade.exam_type)}</dd>
                                </div>
                                <div>
                                  <dt className="text-gray-500">Méthode</dt>
                                  <dd className="font-medium">{getMethodText(grade.exam_method)}</dd>
                                </div>
                                <div>
                                  <dt className="text-gray-500">Titre</dt>
                                  <dd className="font-medium">{grade.exam_title || '-'}</dd>
                                </div>
                              </dl>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-gray-900 mb-3">Commentaire</h4>
                              <div className="bg-white p-3 rounded-md border border-gray-200 min-h-[80px] text-sm">
                                {grade.comment || <span className="text-gray-400">Aucun commentaire</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default StudentGrades;